import * as React from "react"
import { X, ChevronDown, Plus } from "lucide-react"
import { Badge } from "./badge"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Select, SelectContent, SelectTrigger, SelectValue } from "./select"
import { Input } from "./input"

export type Option = {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  className?: string
  placeholder?: string
  isSearchable?: boolean
  showAddButton?: boolean
  addButtonText?: string
  onAddButtonClick?: () => void
  maxHeight?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  placeholder = "Select options...",
  isSearchable = true,
  showAddButton = false,
  addButtonText = "Add New",
  onAddButtonClick,
  maxHeight = "200px",
  ...props
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isRemovingItem, setIsRemovingItem] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const selectTriggerRef = React.useRef<HTMLButtonElement>(null)
  const optionsListRef = React.useRef<HTMLDivElement>(null)

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return options
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [options, searchTerm])

  // Handle option selection/deselection
  const handleOptionToggle = (value: string) => {
    const isSelected = selected.includes(value)
    if (isSelected) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Handle search input blur
  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement
    if (relatedTarget && !relatedTarget.closest('[role="listbox"]')) {
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          const activeElement = document.activeElement as HTMLElement
          if (!activeElement?.closest('[role="listbox"]')) {
            setIsOpen(false)
          }
        }
      }, 100)
    }
  }

  // Handle search input key events
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault()
      handleOptionToggle(filteredOptions[0].value)
    }
    
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
      selectTriggerRef.current?.focus()
    }
  }

  // Handle mouse down on search input
  const handleSearchMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }

  const handleDropdownWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const optionsList = optionsListRef.current
    if (!optionsList || optionsList.scrollHeight <= optionsList.clientHeight) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    optionsList.scrollTop += e.deltaY
  }

  // Handle add new button click
  const handleAddNew = () => {
    onAddButtonClick?.()
    setIsOpen(false)
    setSearchTerm('')
  }

  // Clear search when select closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  // Handle unselect from badges
  const handleUnselect = (option: string) => {
    setIsRemovingItem(true)
    onChange(selected.filter((s) => s !== option))
    setTimeout(() => setIsRemovingItem(false), 150)
  }

  // Handle keyboard navigation for badges
  const handleBadgeKeyDown = (e: React.KeyboardEvent, option: string) => {
    if (e.key === "Enter" || e.key === "Delete" || e.key === "Backspace") {
      handleUnselect(option)
    }
  }

  return (
    <div className={`relative ${className}`}>
      <Select
        open={isOpen && !isRemovingItem}
        onOpenChange={(open) => {
          if (isRemovingItem && open) {
            return // Prevent opening while removing
          }
          setIsOpen(open)
        }}
      >
        <SelectTrigger 
          ref={selectTriggerRef}
          className={cn(
            "text-color-gray-900 text-left overflow-hidden",
            selected.length > 0 ? "h-auto min-h-9" : "h-9"
          )}
        >
          <div className="flex w-full flex-wrap items-center gap-1 min-h-[1.25rem] overflow-hidden">
            {selected.length > 0 ? (
              selected.map((option) => {
                const selectedOption = options.find((o) => o.value === option)
                return (
                  <Badge
                    key={option}
                    variant="secondary"
                    className="rounded-md border border-transparent bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 font-normal text-blueprint-blue transition-colors hover:border-hero-red-20 hover:bg-hero-red-10 hover:text-readable-red"
                  >
                    {selectedOption?.label || `Unknown (${option})`}
                    <button
                      type="button"
                      className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-hero-red focus:ring-offset-1 relative z-50"
                      onKeyDown={(e) => handleBadgeKeyDown(e, option)}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("onMouseDown triggered for", option)
                        handleUnselect(option)
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("onClick triggered for", option)
                        handleUnselect(option)
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation()
                        console.log("onPointerDown triggered for", option)
                      }}
                      aria-label={`Remove ${selectedOption?.label}`}
                    >
                      <X className="h-3 w-3 text-heritage-grey hover:text-readable-red" />
                    </button>
                  </Badge>
                )
              })
            ) : (
              <span className="truncate text-heritage-grey">{placeholder}</span>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="max-h-[min(24rem,calc(100vh-8rem))] overflow-hidden">
          <div className="flex max-h-[min(12rem,calc(100vh-8rem))] flex-col" onWheel={handleDropdownWheel}>
            {/* Search Input */}
            {isSearchable && (
              <div className="p-2 border-b border-[#E2E5EA] flex-shrink-0 h-12">
                <Input
                  ref={searchInputRef}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  onMouseDown={handleSearchMouseDown}
                  className="h-8 text-[13px] placeholder:text-heritage-grey"
                  autoFocus={false}
                />
              </div>
            )}
            
            {/* Options List */}
            <div 
              ref={optionsListRef}
              className="flex-1 overflow-y-auto min-h-0"
              style={{ maxHeight }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const isSelected = selected.includes(option.value)
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 px-2 py-1.5 rounded-sm",
                        "hover:bg-page-bg",
                        "text-text-grey"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleOptionToggle(option.value)
                      }}
                    >
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-[#E2E5EA]",
                          isSelected
                            ? "bg-hero-red border-hero-red text-white"
                            : "bg-white"
                        )}
                      >
                        {isSelected && (
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-[13px]">{option.label}</span>
                    </div>
                  )
                })
              ) : (
                <div className="p-2 text-center text-heritage-grey text-[13px]">
                  {searchTerm ? 'No results found' : 'No options available'}
                </div>
              )}
            </div>

            {/* Add New Button */}
            {showAddButton && onAddButtonClick && (
              <div className="border-t border-[#E2E5EA] p-1 flex-shrink-0 h-10 flex items-center justify-center">
                <Button
                  className="flex items-center text-[13px] hover:bg-page-bg text-hero-red h-8 w-full"
                  variant="ghost"
                  onClick={handleAddNew}
                  type="button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {addButtonText}
                </Button>
              </div>
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  )
} 